import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Button,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  Box,
  Paper,
  Chip,
  Divider,
  Alert,
  Collapse
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { api as axios } from '../../api';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import Transactions from '../Transactions/transaction.table';
import ProChart from '../../components/ProChart/ProChart';
import {
  logoWrapper,
  convertToInternationalCurrencySystem,
  toCurrentTerms
} from '../../utils';
import SwapDiv from '../../components/SwapDiv';
import TreasuryBackingPublic from '../../components/TreasuryBackingPublic';

const formatPrice = (value) => {
  if (value == null) return 'N/A';
  return value.toFixed(5);
};

const formatCurrency = (value, decimals = 2) => {
  if (value == null) return 'N/A';
  return `$${value.toLocaleString(undefined, {
    maximumFractionDigits: decimals
  })}`;
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const boolLabel = (val) => (val === 1 ? 'Yes' : 'No');
const boolColor = (val) => (val === 1 ? '#10B981' : '#9ca3af');

// ── Shared sub-components ──────────────────────────────────────────────────

const DetailRow = ({ label, value, color = '#f0f0f0' }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.75,
      gap: 2
    }}
  >
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: '#fff', minWidth: '45%' }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ color, textAlign: 'right', flex: 1, wordBreak: 'break-word' }}
    >
      {value !== null && value !== undefined && value !== '' ? value : 'N/A'}
    </Typography>
  </Box>
);

const DetailBlock = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: '#fff', mb: 0.5 }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: '#f0f0f0', wordBreak: 'break-word', lineHeight: 1.6 }}
    >
      {value !== null && value !== undefined && value !== '' ? value : 'N/A'}
    </Typography>
  </Box>
);

const SectionBox = ({ title, children }) => (
  <Box
    sx={{
      borderRadius: 2,
      p: 2.5,
      backgroundColor: 'rgba(16, 185, 129, 0.03)'
    }}
  >
    <Typography
      variant="subtitle2"
      sx={{
        color: '#10B981',
        mb: 2,
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.75rem'
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const StackedRows = ({ children }) => (
  <Stack
    spacing={0}
    divider={<Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />}
  >
    {children}
  </Stack>
);

// ── Main Component ─────────────────────────────────────────────────────────

// Detail page for Venture tokens. Also serves holding tokens (listed on the
// VentureTokens page alongside project tokens) so both open the same layout
// as the other token pages, instead of the older holding-token screen.
const SOURCES = {
  project: { endpoint: '/hootdex/get_my_tokens_project', typeLabel: 'Venture' },
  holding: { endpoint: '/wallet/get_my_tokens_holding', typeLabel: 'Holding' },
  // Nexus tokens (formerly FanTokens).
  nexus: { endpoint: '/hootdex/get_my_tokens_fan', typeLabel: 'Nexus' }
};

export default function ProjectToken({ user, kind = 'project' }) {
  const source = SOURCES[kind] || SOURCES.project;
  const { tokenSymbol: token_symbol } = useParams();
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  // 'ok' | 'notfound' | 'error' - a missing token used to crash the page
  // (the API answers { tokens: [] }, not { token: [] }).
  const [status, setStatus] = useState('ok');
  const [alert, setAlert] = useState({ msg: '', type: '', show: false });
  // Live per-market Order Book Depth, polled globally in App.js - the same
  // figure the listing's OBD column shows.
  const orderBookDepth = useSelector((state) => state.stats?.orderBookDepth);

  useEffect(() => {
    if (!token_symbol) return;
    let cancelled = false;

    // /hootdex/get_my_tokens_project overlays the live 24h volume (the raw
    // project_tokens.volume column is never updated), so the page and the
    // listing show the same number.
    const load = (initial) => {
      if (initial) setLoading(true);
      return axios
        .get(source.endpoint, {
          params: { symbol: token_symbol }
        })
        .then((res) => {
          if (cancelled) return;
          const row = res.data?.tokens?.[0] || res.data?.token?.[0] || null;
          if (row) {
            setToken(row);
            setStatus('ok');
          } else if (initial) {
            setToken(null);
            setStatus('notfound');
          }
        })
        .catch((err) => {
          console.error('[ProjectToken] fetch failed:', err.message);
          if (cancelled) return;
          if (initial) {
            setStatus('error');
          } else {
            setAlert({ msg: 'Live update failed', type: 'error', show: true });
            setTimeout(
              () => setAlert({ msg: '', type: 'error', show: false }),
              3000
            );
          }
        })
        .finally(() => {
          if (!cancelled && initial) setLoading(false);
        });
    };

    setToken(null);
    setStatus('ok');
    load(true);
    const timer = setInterval(() => load(false), 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [token_symbol, source.endpoint]);

  // Change vs 1h ago, same convention as every other token page:
  // (current - 1h ago) / 1h ago. This used to be computed the other way
  // round, so a falling price showed a green up arrow.
  const currentPrice = Number(token?.token_price);
  const price1h = Number(token?.price1hour);
  const hasChange =
    Number.isFinite(currentPrice) && Number.isFinite(price1h) && price1h > 0;
  const priceChange = hasChange ? currentPrice - price1h : 0;
  const priceChangePercent = hasChange
    ? Math.abs((priceChange / price1h) * 100)
    : 0;
  const isPositive = priceChange >= 0;

  const obd = orderBookDepth?.[token?.token_symbol ?? token_symbol];

  return (
    <>
      <Helmet>
        <title>{`${token_symbol} ${
          token?.token_price?.toFixed(5) ?? ''
        }`}</title>
      </Helmet>

      {loading && (
        <Box>
          <LinearProgress sx={{ backgroundColor: 'grey' }} />
        </Box>
      )}

      {!loading && status !== 'ok' && (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
            {status === 'notfound'
              ? `${source.typeLabel} token "${token_symbol}" was not found`
              : `Could not load this ${source.typeLabel} token`}
          </Typography>
          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
            {status === 'notfound'
              ? 'It may have been delisted or deactivated.'
              : 'Please refresh the page in a moment.'}
          </Typography>
        </Box>
      )}

      {!loading && status === 'ok' && token && (
        <Box sx={{ p: { xs: 2, md: 4 }, ml: { xs: 4 } }}>
          <Grid container spacing={3} sx={{ ml: '-55px' }}>
            <SwapDiv token={token_symbol} />

            {/* ══════════════════════════════════════
                LEFT COLUMN — Token Info
            ══════════════════════════════════════ */}
            <Grid item xs={12} md={5} mt={4} sx={{ order: { xs: 1, md: 0 } }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: 'rgba(4, 10, 8, 0.72)',
                  borderRadius: 3,
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}
              >
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={logoWrapper(token?.image)}
                    alt={token_symbol}
                    sx={{ width: 56, height: 56 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}
                    >
                      {token?.token_symbol ?? token_symbol}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                      {toCurrentTerms(token?.token_name)}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Price */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#9ca3af',
                      mb: 1.5,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    {token?.token_symbol} Price
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 2,
                      flexWrap: 'wrap'
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ color: 'white', fontWeight: 600 }}
                    >
                      ${formatPrice(token?.token_price)}
                    </Typography>
                    {hasChange && (
                        <Chip
                          icon={
                            isPositive ? (
                              <ArrowUpwardIcon sx={{ fontSize: '16px' }} />
                            ) : (
                              <ArrowDownwardIcon sx={{ fontSize: '16px' }} />
                            )
                          }
                          label={`${priceChangePercent.toFixed(2)}%`}
                          size="medium"
                          sx={{
                            backgroundColor: isPositive
                              ? 'rgba(16, 185, 129, 0.15)'
                              : 'rgba(239, 83, 80, 0.15)',
                            color: isPositive ? '#10B981' : '#EF5350',
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            '& .MuiChip-icon': { color: 'inherit' }
                          }}
                        />
                      )}
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Key Information */}
                <SectionBox title="Key Information">
                  <StackedRows>
                    <DetailRow label="Issuer" value={token?.token_issuer} />
                    <DetailRow
                      label="Token Symbol"
                      value={token?.token_symbol}
                    />
                    <DetailRow label="Token Name" value={toCurrentTerms(token?.token_name)} />
                    <DetailRow
                      label="Instrument Type"
                      value={token?.venture_main_type}
                    />
                    <DetailRow label="Type" value={source.typeLabel} />
                    <DetailRow
                      label="Currency"
                      value={token?.currency ?? 'USD'}
                    />
                    <DetailRow
                      label="Amount Issued"
                      value={token?.amount_issued?.toLocaleString()}
                    />
                    <DetailRow
                      label="Token Amount"
                      value={token?.token_amount?.toLocaleString()}
                    />
                    <DetailRow
                      label="Face Value"
                      value={
                        token?.face_value != null
                          ? formatCurrency(token.face_value)
                          : 'N/A'
                      }
                    />
                    <DetailRow
                      label="Launch Price"
                      value={
                        token?.launch_price != null
                          ? `$${token.launch_price}`
                          : 'N/A'
                      }
                    />

                    <DetailRow
                      label="Status"
                      value={token?.status === 1 ? 'Active' : 'Inactive'}
                      color={token?.status === 1 ? '#10B981' : '#EF5350'}
                    />

                    <DetailRow
                      label="Created"
                      value={formatDate(token?.date_time)}
                    />
                    <DetailRow
                      label="Last Updated"
                      value={formatDate(token?.timestamp)}
                    />
                  </StackedRows>
                </SectionBox>

                {/* Market Data */}
                <SectionBox title="Market Data">
                  <StackedRows>
                    <DetailRow
                      label="Volume (24H)"
                      value={
                        token?.volume != null
                          ? '$' +
                            convertToInternationalCurrencySystem(token.volume)
                          : 'N/A'
                      }
                    />
                    {/* Live bids + asks for this market - replaces the old
                        static TVL figure, same as the listing's OBD column. */}
                    <DetailRow
                      label="Order Book Depth (OBD)"
                      value={
                        Number.isFinite(obd)
                          ? '$' + convertToInternationalCurrencySystem(obd)
                          : 'N/A'
                      }
                    />
                    <DetailRow
                      label="Market Cap"
                      value={
                        token?.amount_issued && token?.token_price
                          ? '$' +
                            convertToInternationalCurrencySystem(
                              token.amount_issued * token.token_price
                            )
                          : 'N/A'
                      }
                    />
                  </StackedRows>
                </SectionBox>

                {/* Capabilities */}
                {/* <SectionBox title="Capabilities">
                  <StackedRows>
                    <DetailRow
                      label="MVault Capable"
                      value={boolLabel(token?.mvault_capable)}
                      color={boolColor(token?.mvault_capable)}
                    />
                    <DetailRow
                      label="Escrow Capable"
                      value={boolLabel(token?.escrow_capable)}
                      color={boolColor(token?.escrow_capable)}
                    />
                    <DetailRow
                      label="Liquidity Pool Capable"
                      value={boolLabel(token?.liquidityPool_capable)}
                      color={boolColor(token?.liquidityPool_capable)}
                    />
                    <DetailRow
                      label="Pecu 500"
                      value={boolLabel(token?.pecu_500)}
                      color={boolColor(token?.pecu_500)}
                    />
                    <DetailRow
                      label="HootDex Listing"
                      value={boolLabel(token?.hootdex_listing)}
                      color={boolColor(token?.hootdex_listing)}
                    />
                    <DetailRow
                      label="Has Pool"
                      value={boolLabel(token?.hasPool)}
                      color={boolColor(token?.hasPool)}
                    />
                  </StackedRows>
                </SectionBox> */}

                {/* Risk & Credit */}
                <SectionBox title="Risk & Credit">
                  <StackedRows>
                    <DetailRow
                      label="Credit Grade"
                      value={token?.credit_grade}
                    />
                    <DetailRow
                      label="Default Risk"
                      value={token?.default_risk}
                    />
                    <DetailRow
                      label="Liquidity Risk"
                      value={token?.liquidity_risk}
                    />
                    <DetailRow
                      label="Operational Risk"
                      value={token?.operational_risk}
                    />
                    <DetailRow
                      label="Smart Contract Risk"
                      value={token?.smart_contract_risk}
                    />
                    <DetailRow
                      label="Collateralization Level"
                      value={token?.collateralization_level}
                    />
                    <DetailRow
                      label="Asset Coverage Ratio"
                      value={token?.asset_coverage_ratio}
                    />
                    <DetailRow
                      label="Concentration Risk"
                      value={token?.concentration_risk}
                    />
                    <DetailRow
                      label="Regulatory Risk"
                      value={token?.regulatory_risk}
                    />
                  </StackedRows>
                </SectionBox>

                {/* Action Buttons */}
                <Stack spacing={2}>
                  <Button
                    variant="outlined"
                    color="success"
                    href="https://pecuscan.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      borderWidth: 2,
                      fontWeight: 600,
                      '&:hover': { borderWidth: 2 }
                    }}
                  >
                    View on Pecuscan
                  </Button>

                  <Button
                    variant="contained"
                    href={`https://app.hootdex.net/${encodeURIComponent(
                      token.token_symbol
                    )}`}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      backgroundColor: '#10B981',
                      color: 'white',
                      fontWeight: 600,
                      '&:hover': { backgroundColor: '#059669' }
                    }}
                  >
                    Trade {token.token_symbol}
                  </Button>

                  {token?.link && (
                    <Button
                      variant="outlined"
                      color="success"
                      href={token.link}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        borderWidth: 2,
                        fontWeight: 600,
                        '&:hover': { borderWidth: 2 }
                      }}
                    >
                      Learn More
                    </Button>
                  )}
                </Stack>
              </Paper>
            </Grid>

            {/* ══════════════════════════════════════
                RIGHT COLUMN — Chart + Extended Details
            ══════════════════════════════════════ */}
            <Grid
              item
              xs={12}
              md={7}
              sx={{ marginTop: { xs: 4, md: 4 }, order: { xs: 0, md: 1 } }}
            >
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: 'rgba(4, 10, 8, 0.72)',
                  borderRadius: 3,
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}
              >
                {/* Chart */}
                <Box sx={{ minHeight: 400 }}>
                  {/* Chart series are keyed by token_symbol (MLABx, ...);
                      identity_symbol/symbol are empty on these rows. */}
                  <ProChart
                    symbol={token?.token_symbol || token_symbol}
                    chartSymbol={token?.token_symbol || token?.identity_symbol || token_symbol}
                  />
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* About / Description */}
                {token?.token_desc && (
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: '#10B981', mb: 2, fontWeight: 700 }}
                    >
                      About {toCurrentTerms(token.token_name)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#f0f0f0', lineHeight: 1.8 }}
                    >
                      {toCurrentTerms(token.token_desc)}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {kind === 'project' && (
                  <TreasuryBackingPublic
                    tokenType="PROJECT"
                    tokenId={token?.id}
                  />
                )}

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Project Details */}
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Project Details
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Project Name"
                        value={token?.project_name}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Project Stage"
                        value={token?.project_stage}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Project Location"
                        value={token?.project_location}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Revenue Model"
                        value={token?.revenue_model}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Venture Type Code"
                        value={token?.venture_type_code}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock label="Sort Order" value={token?.sort} />
                    </Grid>
                    <Grid item xs={12}>
                      <DetailBlock
                        label="Project Risks"
                        value={token?.project_risks}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Custody & Treasury */}
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Custody & Treasury
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Custody Types"
                        value={token?.custody_types}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Treasury Type"
                        value={token?.treasury_type}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DetailBlock
                        label="Treasury Key"
                        value={token?.treasury_key}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            {/* ── Transactions Table ── */}
            <Grid item xs={12} mt={2} sx={{ order: { xs: 2, md: 2 } }}>
              <Transactions />
            </Grid>
          </Grid>

          {/* Alert */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Collapse in={alert.show} sx={{ maxWidth: 400, position: 'fixed' }}>
              <Alert
                variant="outlined"
                severity={alert.type}
                sx={{ mb: 2, backgroundColor: 'white', fontSize: '18px' }}
              >
                {alert.msg}
              </Alert>
            </Collapse>
          </Box>
        </Box>
      )}
    </>
  );
}
